import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppContext } from '../enums/AppContext.enum';
import { RemoteAccessConfig } from '../models/RemoteAccessConfig.model';
import { SessionSettings } from '../models/SessionSettings.model';
import { UtilService } from './util.service';

// TODO: Move this to the remote configuration API if the need arises to modify it without a code change. 
//       Otherwise, keep it here and remove this comment
const CCU_STATUS_POLL_FREQUENCY_MS = 5000;
const CCU_INSESSION_POLL_FREQUENCY_MS = 1000;

// Number of seconds before the user is prompted with a "CCU unreachable" message.
// This value is local to the current UI session and is not persisted nor communicated 
// to the backend or RAA.
const STALE_SESSION_THRESHOLD_SECONDS = 30;

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  /**
   * The global configuration provided by the API once the virtual tablet component is loaded
   */
  remoteAccessConfig: RemoteAccessConfig;

  sessionId: string;
  debugModeEnabled: boolean;
  hostApp: string;
  appContext: AppContext;
  targetEntityId: string;

  ccuStatusPollFrequencyMs: number = CCU_STATUS_POLL_FREQUENCY_MS;
  staleSessionThresholdSeconds: number = STALE_SESSION_THRESHOLD_SECONDS;

  configChangeSubject: Subject<RemoteAccessConfig> =
    new Subject<RemoteAccessConfig>();

  regionCode: string | null = null;

  constructor(private utilService: UtilService) {}

  private getMultiRegionConfig(): any {
    return (environment as any).multiRegion;
  }

  initializeRegionFromUrl(): void {
    const multiRegion = this.getMultiRegionConfig();

    if (!multiRegion?.enabled) {
      this.regionCode = null;
      return;
    }

    const regionId = this.utilService.getUrlQueryParam('regionId');
    const isKnownRegionId =
      regionId && multiRegion.regions && multiRegion.regions[regionId];

    // regionId mapping: 1 => NA, 2 => EU
    this.regionCode = isKnownRegionId
      ? regionId
      : multiRegion.defaultRegion ?? null;
  }

  private getMultiRegionValue(key: string): any {
    const multiRegion = this.getMultiRegionConfig();

    if (!multiRegion?.enabled || !this.regionCode) {
      return undefined;
    }

    return multiRegion.regions?.[this.regionCode]?.[key];
  }

  get(key: string) {
    const regionValue = this.getMultiRegionValue(key);

    if (regionValue !== undefined) {
      return regionValue;
    }

    return (environment as any)[key];
  }

    // Stale Session Threshold only applies to the current UI session and is not 
  // persisted nor communicated to the backend or RAA.
  getStaleSessionThresholdSeconds(): number {
    return this.staleSessionThresholdSeconds;
  }

  setStaleSessionThresholdSeconds(seconds: number = STALE_SESSION_THRESHOLD_SECONDS): void {
    this.staleSessionThresholdSeconds = seconds;
  }

  updateSessionSettings(sessionSettings: SessionSettings): void {
    this.remoteAccessConfig.quality = sessionSettings.qualityPercentage;
    this.remoteAccessConfig.refreshFrequency =
      sessionSettings.refreshIntervalMillis;
    this.remoteAccessConfig.uploadFrequency =
      sessionSettings.refreshIntervalMillis;
    this.remoteAccessConfig.commandPollMillis = sessionSettings.commandPollMillis;

    this.configChangeSubject.next(this.remoteAccessConfig);
  }
}
